import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getQuickOrderList,
  getQuickOrderDetail,
  cancelQuickOrder,
  checkQuickOrderPrice,
  saveQuickOrder,
  getQuickOrderStat,
  getAddressBookList,
  saveAddressBook,
  deleteAddressBook,
  getPickerInfo,
  getTermLatest,
  getTermDate,
  getTerm,
  getQuickUserAuthList,
  setQuickUserUse,
  setQuickUserUseBulk
} from 'apis/captainpayment/quick';
import { getMemberAuth } from 'apis/captainpayment/member';
import toastr from 'helpers/toastr';

import {
  IQuickOrderList,
  IOrderAddressList,
  IQuickOrderAddress,
  IMemberServiceAuth,
  IQuickUserAuthList,
  IQuickUserAuthParams,
  IQuickUserUseBulkParams,
  IQuickUserUseParams
} from './types';

// 퀵 주문 목록 조회
export const useGetQuickOrderList = (params: unknown) => {
  return useQuery<IQuickOrderList>(['getQuickOrderList', params], () => getQuickOrderList(params));
};

// 엑셓용 퀵 주문 목록 조회
export const useGetQuickOrderExcelList = () => {
  return useMutation(['getQuickOrderList'], (params: unknown) => getQuickOrderList(params));
};

// 퀵 주문 상세 조회
export const useGetQuickOrderDetail = () => {
  return useMutation(['getQuickOrderDetail'], (quickIdx: number) => getQuickOrderDetail(quickIdx));
};

// 퀵 배송 주문 취소
export const useCancelQuickOrder = () => {
  const queryClient = useQueryClient();
  return useMutation(['cancelQuickOrder'], (quickIdx: number) => cancelQuickOrder(quickIdx), {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['getQuickOrderList'] })
  });
};

// 퀵 배송 예상 가격 조회
export const useCheckQuickOrderPrice = () => {
  return useMutation(['checkQuickOrderPrice'], (params: unknown) => checkQuickOrderPrice(params));
};

// 퀵 배송 주문 생성
export const useSaveQuickOrder = () => {
  return useMutation(['saveQuickOrder'], (params: unknown) => saveQuickOrder(params));
};

// 퀵 서비스 통계 조회
export const useGetQuickOrderStat = (params: unknown) => {
  return useQuery(['getQuickOrderStat', params], () => getQuickOrderStat(params));
};

// 주소록 목록 조회
export const useAddressBookList = (isAll: boolean) => {
  return useQuery<IOrderAddressList>(['getAddressBookList'], () => getAddressBookList({ isAll }));
};

// 주소록 항목 추가
export const useSaveAddressBook = () => {
  const queryClient = useQueryClient();
  return useMutation(['saveAddressBook'], (params: IQuickOrderAddress) => saveAddressBook(params), {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['getAddressBookList'] })
  });
};

// 주소록 항목 삭제
export const useDeleteAddressBook = () => {
  const queryClient = useQueryClient();

  return useMutation(['deleteAddressBook'], (addressIdx: number) => deleteAddressBook(addressIdx), {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['getAddressBookList'] })
  });
};

export const useGetPickerInfo = (quickIdx?: number, enabled?: boolean) => {
  return useQuery(['getPickerInfo', quickIdx, enabled], () => getPickerInfo(quickIdx), {
    enabled,
    onError: () => toastr('top-right', 'warning', '배송기사 위치 조회에 실패 하였습니다. 다시 한번 시도해주세요.')
  });
};

const QUICK_USER_AUTH_KEY = 'getQuickUserAuthList';

// 서비스별 메뉴 권한 매핑표 (거의 변하지 않아 오래 캐시한다)
export const useGetMemberAuth = () => {
  return useQuery<IMemberServiceAuth[]>(['getMemberAuth'], () => getMemberAuth(), { staleTime: 10 * 60 * 1000 });
};

// 퀵 사용자 권한 목록 조회
export const useGetQuickUserAuthList = (params: IQuickUserAuthParams) => {
  return useQuery<IQuickUserAuthList>([QUICK_USER_AUTH_KEY, params], () => getQuickUserAuthList(params), {
    keepPreviousData: true
  });
};

// 사용자별 퀵 사용여부 변경
export const useSetQuickUserUse = () => {
  const queryClient = useQueryClient();

  return useMutation(['setQuickUserUse'], (params: IQuickUserUseParams) => setQuickUserUse(params), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUICK_USER_AUTH_KEY] });
      toastr('top-right', 'success', '퀵대장 사용여부가 변경되었습니다.');
    },
    onError: () => toastr('top-right', 'warning', '퀵대장 사용여부 변경에 실패했습니다. 다시 한번 시도해주세요.')
  });
};

// 선택 사용자 퀵 사용여부 일괄 변경
export const useSetQuickUserUseBulk = () => {
  const queryClient = useQueryClient();

  return useMutation(['setQuickUserUseBulk'], (params: IQuickUserUseBulkParams) => setQuickUserUseBulk(params), {
    // 사용자별로 요청하므로 일부만 실패할 수 있다. 목록은 어느 쪽이든 갱신한다.
    // 처리·제외 건수는 호출한 화면만 알기에 결과 안내도 그쪽에서 한다
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUICK_USER_AUTH_KEY] }),
    onError: () => toastr('top-right', 'warning', '퀵대장 사용여부 변경에 실패했습니다. 다시 한번 시도해주세요.')
  });
};

export const useGetTermLatest = (params: unknown) => {
  return useQuery(['getTermLatest', params], () => getTermLatest(params));
};

export const useGetTerm = (params: unknown) => {
  return useQuery(['getTerm', params], () => getTerm(params));
};

export const useGetTermDate = (params: unknown) => {
  return useQuery(['getTermDate', params], () => getTermDate(params));
};
